// frontend/src/components/PhraseManager/PhraseManager.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from '../../UserContext';
import {
  getPhrases,
  createPhrase,
  updatePhrase,
  deletePhrase,
  createClinicPhrase,
  updateClinicPhrase,
  deleteClinicPhrase
} from '../JournalEditor/api/journalApi';
import './PhraseManager.css';

function PhraseManager({ onClose }) {
  const { user } = useContext(UserContext);
  const isAdmin = user?.is_admin || false;

  // State
  const [activeTab, setActiveTab] = useState('personal');
  const [personalPhrases, setPersonalPhrases] = useState([]);
  const [clinicPhrases, setClinicPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPhrase, setEditingPhrase] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    phrase_text: ''
  });
  const [saving, setSaving] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Load phrases
  const loadPhrases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPhrases();
      setPersonalPhrases(data.personal_phrases || []);
      setClinicPhrases(data.clinic_phrases || []);
    } catch (err) {
      console.error('Error loading phrases:', err);
      setError('Kunde inte ladda fraser');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhrases();
  }, [loadPhrases]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      phrase_text: ''
    });
    setEditingPhrase(null);
    setShowForm(false);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (phrase) => {
    setFormData({
      title: phrase.title || '',
      phrase_text: phrase.phrase_text || ''
    });
    setEditingPhrase(phrase);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Kortkommando krävs');
      return;
    }
    if (!formData.phrase_text.trim()) {
      alert('Text krävs');
      return;
    }

    try {
      setSaving(true);

      if (editingPhrase) {
        // Update existing
        if (activeTab === 'clinic') {
          await updateClinicPhrase(editingPhrase.phrase_id, formData);
        } else {
          await updatePhrase(editingPhrase.phrase_id, formData);
        }
      } else {
        // Create new
        if (activeTab === 'clinic') {
          await createClinicPhrase(formData);
        } else {
          await createPhrase(formData);
        }
      }

      await loadPhrases();
      resetForm();
    } catch (err) {
      console.error('Error saving phrase:', err);
      alert(err.response?.data?.error || 'Kunde inte spara fras');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (phrase) => {
    if (!window.confirm(`Vill du ta bort "${phrase.title}"?`)) {
      return;
    }

    try {
      if (activeTab === 'clinic') {
        await deleteClinicPhrase(phrase.phrase_id);
      } else {
        await deletePhrase(phrase.phrase_id);
      }
      await loadPhrases();
    } catch (err) {
      console.error('Error deleting phrase:', err);
      alert(err.response?.data?.error || 'Kunde inte ta bort fras');
    }
  };

  // Filter phrases
  const filterPhrases = (phrases) => {
    if (!searchQuery) return phrases;
    const query = searchQuery.toLowerCase();
    return phrases.filter(p =>
      p.title.toLowerCase().includes(query) ||
      p.phrase_text.toLowerCase().includes(query)
    );
  };

  const currentPhrases = activeTab === 'clinic' ? clinicPhrases : personalPhrases;
  const filteredPhrases = filterPhrases(currentPhrases);

  // Sort alphabetically by title
  const sortedPhrases = [...filteredPhrases].sort((a, b) =>
    a.title.localeCompare(b.title, 'sv')
  );

  return (
    <div className="phrase-manager-overlay" onClick={onClose}>
      <div className="phrase-manager" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="phrase-manager-header">
          <h2>
            <i className="fas fa-bolt"></i>
            Snabbfraser
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Info banner */}
        <div className="phrase-info-banner">
          <i className="fas fa-info-circle"></i>
          <span>
            Skriv <kbd>;</kbd> följt av kortkommandot i en journal för att infoga frasen.
            Exempel: <kbd>;status</kbd>
          </span>
        </div>

        {/* Tabs */}
        <div className="phrase-manager-tabs">
          <button
            className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            <i className="fas fa-user"></i>
            Mina fraser ({personalPhrases.length})
          </button>
          {isAdmin && (
            <button
              className={`tab-btn ${activeTab === 'clinic' ? 'active' : ''}`}
              onClick={() => setActiveTab('clinic')}
            >
              <i className="fas fa-building"></i>
              Klinikfraser ({clinicPhrases.length})
            </button>
          )}
        </div>

        {/* Toolbar */}
        <div className="phrase-manager-toolbar">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Sök fraser..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="add-btn" onClick={handleAddNew}>
            <i className="fas fa-plus"></i>
            Ny fras
          </button>
        </div>

        {/* Content */}
        <div className="phrase-manager-content">
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              Laddar fraser...
            </div>
          ) : error ? (
            <div className="error-state">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
              <button onClick={loadPhrases}>Försök igen</button>
            </div>
          ) : sortedPhrases.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-clipboard-list"></i>
              <p>
                {searchQuery
                  ? 'Inga fraser matchar din sökning'
                  : activeTab === 'clinic'
                    ? 'Inga klinikfraser ännu.'
                    : 'Inga personliga fraser ännu.'
                }
              </p>
              {!searchQuery && (
                <button onClick={handleAddNew}>
                  <i className="fas fa-plus"></i>
                  Skapa din första fras
                </button>
              )}
            </div>
          ) : (
            <table className="phrase-table">
              <thead>
                <tr>
                  <th>Kortkommando</th>
                  <th>Text</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {sortedPhrases.map(phrase => (
                  <tr key={phrase.phrase_id}>
                    <td className="phrase-title-cell">
                      <code>;{phrase.title}</code>
                    </td>
                    <td className="phrase-text-cell">
                      <div className="phrase-text-preview">
                        {phrase.phrase_text}
                      </div>
                    </td>
                    <td className="phrase-actions-cell">
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(phrase)}
                        title="Redigera"
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(phrase)}
                        title="Ta bort"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="phrase-form-overlay" onClick={resetForm}>
            <div className="phrase-form" onClick={e => e.stopPropagation()}>
              <div className="phrase-form-header">
                <h3>{editingPhrase ? 'Redigera fras' : 'Ny fras'}</h3>
                <button className="close-btn" onClick={resetForm}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Kortkommando *</label>
                  <div className="shortcut-input-wrapper">
                    <span className="shortcut-prefix">;</span>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="t.ex. status"
                      required
                      autoFocus
                    />
                  </div>
                  <span className="hint">
                    Skriv <kbd>;{formData.title || 'kortkommando'}</kbd> i journalen för att infoga texten
                  </span>
                </div>

                <div className="form-group">
                  <label>Text *</label>
                  <textarea
                    name="phrase_text"
                    value={formData.phrase_text}
                    onChange={handleInputChange}
                    placeholder="Texten som infogas i journalen..."
                    rows={6}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={resetForm}>
                    Avbryt
                  </button>
                  <button type="submit" className="save-btn" disabled={saving}>
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Sparar...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        {editingPhrase ? 'Spara' : 'Skapa'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Info for clinic tab */}
        {activeTab === 'clinic' && !showForm && (
          <div className="phrase-manager-footer">
            <i className="fas fa-info-circle"></i>
            Klinikfraser är tillgängliga för alla användare på kliniken.
          </div>
        )}
      </div>
    </div>
  );
}

export default PhraseManager;
